import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import UsersAutoComplete from "../../Users/UsersAutoComplete";
import { LeadIndustry, LeadSource, LeadStatus, LeadRating } from "../../../utils/enums";
import dayjs from "dayjs";

const FilterComponent = ({ filters, setFilters }) => {
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [checkedFields, setCheckedFields] = useState([
        { fieldName: "UntouchedForDays", label: "Untouched For Days", checked: false },
        { fieldName: "firstName", label: "First Name", checked: false },
        { fieldName: "lastName", label: "Last Name", checked: false },
        { fieldName: "company", label: "Company", checked: false },
        { fieldName: "email", label: "Email", checked: false },
        { fieldName: "phone", label: "Phone", checked: false },
        { fieldName: "leadSource", label: "Lead Source", checked: false },
        { fieldName: "leadStatus", label: "Lead Status", checked: false },
        { fieldName: "industry", label: "Industry", checked: false },
        { fieldName: "rating", label: "Rating", checked: false },
        { fieldName: "annualRevenue", label: "Annual Revenue", checked: false },
        { fieldName: "country", label: "Country", checked: false },
        { fieldName: "city", label: "City", checked: false },
        { fieldName: "street", label: "Street", checked: false },
        { fieldName: "zipCode", label: "Zip Code", checked: false },
        { fieldName: "mobile", label: "Mobile", checked: false },
        { fieldName: "secondaryEmail", label: "Secondary Email", checked: false },
        { fieldName: "skypeId", label: "Skype Id", checked: false },
        { fieldName: "title", label: "Title", checked: false },
        { fieldName: "twitter", label: "Twitter", checked: false },
        { fieldName: "ownerId", label: "Lead Owner", checked: false },
        { fieldName: "createdBy", label: "Created By", checked: false },
        { fieldName: "createdAt", label: "Created Time", checked: false },
        { fieldName: "updatedBy", label: "Modified By", checked: false },

    ]);

    const [filterValues, setFilterValues] = useState({});
    const [filterOperators, setFilterOperators] = useState({});

    // Initialize state when filters prop is received
    useEffect(() => {
        if (filters && filters.length > 0) {
            const updatedCheckedFields = checkedFields.map((field) => {
                const matchingFilter = filters.find(
                    (filter) => filter.fieldName === field.fieldName
                );
                if (matchingFilter) {
                    return { ...field, checked: true };
                }
                return field;
            });

            setCheckedFields(updatedCheckedFields);

            const initialFilterValues = {};
            const initialFilterOperators = {};

            filters.forEach((filter) => {
                initialFilterValues[filter.fieldName] = filter.fieldValue;
                initialFilterOperators[filter.fieldName] = filter.filterOperator;
            });

            setFilterValues(initialFilterValues);
            setFilterOperators(initialFilterOperators);

            // to handle date
            const dateFilter = filters.find(f => f.fieldName === 'createdAt');
            if (dateFilter?.fieldValue) {
                const [fromDateStr, toDateStr] = dateFilter.fieldValue.split(';');
                setFromDate(fromDateStr ? dayjs(fromDateStr) : null);
                setToDate(toDateStr ? dayjs(toDateStr) : null);
            }
        }
    }, [filters]);

    const handleCheckboxChange = (fieldName) => {
        setCheckedFields((prev) =>
            prev.map((field) =>
                field.fieldName === fieldName
                    ? { ...field, checked: !field.checked }
                    : field
            )
        );

        if (!filterValues[fieldName]) {
            setFilterValues({ ...filterValues, [fieldName]: "" });
            if (["leadSource", "leadStatus", "industry", "rating", "annualRevenue", "UntouchedForDays"].includes(fieldName)) {
                setFilterOperators({ ...filterOperators, [fieldName]: 0 }); // operator "Equal" for enums
            }
            else if (["createdAt"].includes(fieldName)) {
                setFilterOperators({ ...filterOperators, [fieldName]: 14 }); // operator "between" for dates
            }
            else {
                setFilterOperators({ ...filterOperators, [fieldName]: 2 }); // Default operator (e.g., "contains")
            }
        } else {
            const updatedValues = { ...filterValues };
            const updatedOperators = { ...filterOperators };
            delete updatedValues[fieldName];
            delete updatedOperators[fieldName];
            setFilterValues(updatedValues);
            setFilterOperators(updatedOperators);
        }
    };

    const handleFilterChange = (fieldName, value) => {
        // for dates we need the format "startDate:endData"
        if (["createdAt"].includes(fieldName)) {
            const { type, value: dateValue } = value; // Destructure here to avoid `type` not being defined
            setFilterValues((prevValues) => {
                const currentDates = prevValues[fieldName]?.split(';') || [null, null];
                const updatedDates = type === 'from'
                    ? [dateValue?.format('YYYY-MM-DD') || '', currentDates[1] || '']
                    : [currentDates[0] || '', dateValue?.format('YYYY-MM-DD') || ''];
                return {
                    ...prevValues,
                    [fieldName]: updatedDates.join(';'),
                };
            });
        }
        else {
            setFilterValues({ ...filterValues, [fieldName]: value });
        }
    };

    const handleOperatorChange = (fieldName, operator) => {
        setFilterOperators({ ...filterOperators, [fieldName]: operator });
    };

    const handleApplyFilter = () => {
        const getFieldType = (fieldName) => {
            // field type needed in backend
            const intFields = ["leadSource", "leadStatus", "industry", "rating", "annualRevenue", "UntouchedForDays"];
            const dateFields = ["createdAt"];
            const relationalFields = [""];

            if (intFields.includes(fieldName)) return 0;
            if (dateFields.includes(fieldName)) return 2;
            if (relationalFields.includes(fieldName)) return 3;

            return 1; // 0 = int, 1 = string
        };

        const filterParams = checkedFields
            .filter((field) => field.checked)
            .map((field) => ({

                fieldName: field.fieldName,
                filterOperator: filterOperators[field.fieldName],
                fieldValue: filterValues[field.fieldName],
                fieldType: getFieldType(field.fieldName) // Assign correct field type dynamically
            }));

        setFilters(filterParams);
    };

    const handleClear = () => {
        setCheckedFields((prev) => prev.map((field) => ({ ...field, checked: false })));
        setFilterValues({});
        setFilterOperators({});
        setFilters([]);
        localStorage.removeItem('leadsFilters');
        setFromDate(null);
        setToDate(null);
    };

    return (
        <Box p={2} border="1px solid #ccc" borderRadius="8px" sx={{
            height: '100%', // Ensures the component takes the full height of the parent
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
        }}>
            <Typography variant="subtitle1" gutterBottom>
                Filter By Fields
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} height="100%" overflow="auto">
                {checkedFields.map(({ fieldName, label, checked }) => (
                    <Box key={fieldName}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checked}
                                    onChange={() => handleCheckboxChange(fieldName)}
                                />
                            }
                            label={label}
                        />
                        {checked && ["firstName", "lastName", "company", "email", "phone", "country", "city", "street", "zipCode", "mobile", "secondaryEmail", "skypeId", "title", "twitter"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Equals</MenuItem>
                                        <MenuItem value={2}>Contains</MenuItem>
                                        <MenuItem value={4}>Starts With</MenuItem>
                                        <MenuItem value={5}>Ends With</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label="Type here"
                                    size="small"
                                    value={filterValues[fieldName] || ""}
                                    onChange={(e) =>
                                        handleFilterChange(fieldName, e.target.value)
                                    }
                                />
                            </Box>
                        )}
                        {checked && ["createdAt"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="From Date"
                                        value={fromDate}
                                        fullWidth
                                        required
                                        onChange={(newDate) => handleFilterChange(fieldName, { type: 'from', value: newDate })}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                size: 'small'
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="To Date"
                                        value={toDate}
                                        fullWidth
                                        required
                                        onChange={(newDate) => handleFilterChange(fieldName, { type: 'to', value: newDate })}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                size: 'small'
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        )

                        }
                        {checked && ["createdBy", "updatedBy", "ownerId"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <UsersAutoComplete
                                    small={true}
                                    userId={filterValues[fieldName]} // Pass the current filter value for the field
                                    setUserId={(value) => handleFilterChange(fieldName, value)} // Call handleFilterChange with the field and selected value
                                />
                            </Box>
                        )}
                        {checked && fieldName === "leadSource" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Is</MenuItem>
                                        <MenuItem value={1}>Is Not</MenuItem>
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    value={LeadSource.find((option) => option.value.toString() === filterValues[fieldName])}
                                    options={LeadSource}
                                    sx={{ width: "100%" }}
                                    onChange={(event, newValue) => handleFilterChange(fieldName, newValue.value.toString())}
                                    renderInput={(params) => <TextField {...params} label="Lead Source" />}
                                />
                            </Box>
                        )}
                        {checked && fieldName === "leadStatus" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Is</MenuItem>
                                        <MenuItem value={1}>Is Not</MenuItem>
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    value={LeadStatus.find((option) => option.value.toString() === filterValues[fieldName])}
                                    options={LeadStatus}
                                    sx={{ width: "100%" }}
                                    onChange={(event, newValue) => handleFilterChange(fieldName, newValue.value.toString())}
                                    renderInput={(params) => <TextField {...params} label="Lead Status" />}
                                />
                            </Box>
                        )}
                        {checked && fieldName === "industry" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Is</MenuItem>
                                        <MenuItem value={1}>Is Not</MenuItem>
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    value={LeadIndustry.find((option) => option.value.toString() === filterValues[fieldName])}
                                    options={LeadIndustry}
                                    sx={{ width: "100%" }}
                                    onChange={(event, newValue) => handleFilterChange(fieldName, newValue.value.toString())}
                                    renderInput={(params) => <TextField {...params} label="Lead Industry" />}
                                />
                            </Box>
                        )}
                        {checked && fieldName === "rating" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Is</MenuItem>
                                        <MenuItem value={1}>Is Not</MenuItem>
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    value={LeadRating.find((option) => option.value.toString() === filterValues[fieldName])}
                                    options={LeadRating}
                                    sx={{ width: "100%" }}
                                    onChange={(event, newValue) => handleFilterChange(fieldName, newValue.value.toString())}
                                    renderInput={(params) => <TextField {...params} label="Lead Rating" />}
                                />
                            </Box>
                        )}
                        {checked && fieldName === "annualRevenue" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        placeholder="Choose Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>=</MenuItem>
                                        <MenuItem value={1}>!=</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label="Type here"
                                    size="small"
                                    value={filterValues[fieldName] || ""}
                                    onChange={(e) =>
                                        handleFilterChange(fieldName, e.target.value)
                                    }
                                />
                            </Box>
                        )}
                        {checked && fieldName === "UntouchedForDays" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label="Number of days"
                                    size="small"
                                    value={filterValues[fieldName] || ""}
                                    onChange={(e) =>
                                        handleFilterChange(fieldName, e.target.value)
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
            <Box mt={2} display="flex" justifyContent="space-between">
                <Button variant="contained" color="primary" onClick={handleApplyFilter}>
                    Apply Filter
                </Button>
                <Button variant="outlined" onClick={handleClear}>
                    Clear
                </Button>
            </Box>
        </Box>
    );
};

export default FilterComponent;
